import React, { useState } from 'react';

export default function TrendingAnalysis({ user, onCreditsUpdate }) {
  const [region, setRegion] = useState('IN');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleAnalyze = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch('https://tube.contentserp.in/api/analysis/trending-keywords', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ region: region, category: '0' })
      });

      const responseData = await response.json();
      
      if (!response.ok) {
        throw new Error(responseData.error || responseData.message || 'Failed to fetch trending topics');
      }

      // Extract data safely based on backend response structure
      const finalData = responseData.data ? responseData.data : responseData;
      setResults(finalData); 
      
      if (responseData.remainingCredits !== undefined) {
        onCreditsUpdate(responseData.remainingCredits);
      } else if (finalData.remainingCredits !== undefined) {
        onCreditsUpdate(finalData.remainingCredits);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="analysis-container" style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', color: '#333', fontFamily: 'Inter, system-ui, sans-serif' }}>
      
      {/* Header Section */}
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ fontSize: '28px', margin: '0 0 10px 0', color: '#111', display: 'flex', alignItems: 'center', gap: '10px' }}>
          🔥 Trending Keywords & Topics
        </h2>
        <p style={{ color: '#666', margin: 0, fontSize: '15px' }}>
          Discover what is going viral on YouTube right now based on regional data.
        </p>
      </div>

      {/* Input Form */}
      <form onSubmit={handleAnalyze} style={{ display: 'flex', gap: '15px', marginBottom: '30px', flexWrap: 'wrap' }}>
        <select 
          value={region} 
          onChange={(e) => setRegion(e.target.value)}
          style={{ 
            padding: '16px 20px', 
            borderRadius: '10px', 
            border: '1px solid #d1d5db', 
            background: '#fff', 
            color: '#333',
            fontSize: '15px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
            outline: 'none',
            minWidth: '220px',
            cursor: 'pointer'
          }}
        >
          <option value="IN">India (IN)</option>
          <option value="US">United States (US)</option>
          <option value="GB">United Kingdom (UK)</option>
          <option value="CA">Canada (CA)</option>
          <option value="AU">Australia (AU)</option>
        </select>
        
        <button 
          type="submit" 
          disabled={loading}
          style={{ 
            padding: '16px 30px', 
            borderRadius: '10px', 
            background: loading ? '#9ca3af' : '#f59e0b', 
            color: 'white', 
            border: 'none', 
            cursor: loading ? 'not-allowed' : 'pointer',
            fontWeight: '600',
            fontSize: '15px',
            transition: 'background 0.2s',
            boxShadow: '0 4px 6px rgba(245, 158, 11, 0.2)'
          }}
        >
          {loading ? '⏳ Scanning Trends...' : 'Find Trends (Costs Credits)'}
        </button>
      </form>

      {/* Error Message */}
      {error && (
        <div style={{ background: '#fef2f2', borderLeft: '4px solid #ef4444', color: '#991b1b', padding: '16px', borderRadius: '8px', marginBottom: '30px', fontSize: '15px' }}>
          <strong>⚠️ Error:</strong> {error}
        </div>
      )}

      {/* Results Section */}
      {results && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '25px', animation: 'fadeIn 0.5s ease-in' }}>
          
          {/* AI Analysis Content Gap Box */}
          {results.aiAnalysis && (
            <div style={{ background: '#fff', padding: '24px', borderRadius: '12px', borderLeft: '4px solid #f59e0b', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', border: '1px solid #f3f4f6' }}>
              <h3 style={{ margin: '0 0 15px 0', display: 'flex', alignItems: 'center', gap: '8px', color: '#1f2937', fontSize: '18px' }}>
                🤖 AI Content Opportunities
              </h3>
              <div style={{ color: '#4b5563', lineHeight: '1.6', fontSize: '15px', whiteSpace: 'pre-wrap', background: '#f9fafb', padding: '16px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                {results.aiAnalysis}
              </div>
            </div>
          )}

          {/* Top Videos Table/List */}
          {results.trendingVideos && results.trendingVideos.length > 0 && (
            <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 15px rgba(0,0,0,0.04)', border: '1px solid #f3f4f6' }}>
              <h3 style={{ margin: '0 0 20px 0', borderBottom: '1px solid #e5e7eb', paddingBottom: '12px', color: '#111', fontSize: '18px' }}>
                📹 Top Trending Videos ({region})
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {results.trendingVideos.map((video, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: '16px', background: '#f9fafb', padding: '16px', borderRadius: '10px', alignItems: 'center', border: '1px solid #f3f4f6', transition: 'transform 0.2s', ':hover': { transform: 'translateY(-2px)' } }}>
                    
                    <div style={{ fontSize: '24px', fontWeight: '800', color: '#d1d5db', minWidth: '40px', textAlign: 'center' }}>
                      #{idx + 1}
                    </div>
                    
<img 
  src={
    video.thumbnails?.medium?.url || 
    video.thumbnails?.high?.url || 
    video.thumbnails?.default?.url || 
    `https://i.ytimg.com/vi/${video.videoId}/mqdefault.jpg`
  } 
  alt={video.title} 
  onError={(e) => { e.target.src = 'https://via.placeholder.com/120x90?text=No+Thumb' }}
  style={{ width: '120px', height: '90px', borderRadius: '8px', objectFit: 'cover', border: '1px solid #e5e7eb' }} 
/>
                    
                    <div style={{ flex: 1 }}>
                      <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', lineHeight: '1.4' }}>
                        <a href={`https://youtube.com/watch?v=${video.videoId}`} target="_blank" rel="noreferrer" style={{ color: '#111', textDecoration: 'none', fontWeight: '600' }}>
                          {video.title}
                        </a>
                      </h4>
                      
                      <div style={{ display: 'flex', gap: '15px', fontSize: '13px', color: '#6b7280' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          📺 <strong style={{color: '#374151', fontWeight: '600'}}>{video.channelTitle}</strong>
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          👀 <strong style={{color: '#374151'}}>{Number(video.viewCount).toLocaleString()} views</strong>
                        </span>
                      </div>
                    </div>
                    
                    {/* Optional: Add a subtle watch button on desktop */}
                    <a 
                      href={`https://youtube.com/watch?v=${video.videoId}`} 
                      target="_blank" 
                      rel="noreferrer"
                      style={{ display: 'none', padding: '8px 16px', background: '#eff6ff', color: '#2563eb', borderRadius: '6px', textDecoration: 'none', fontSize: '13px', fontWeight: '600', border: '1px solid #bfdbfe' }}
                      className="watch-btn"
                    >
                      Watch
                    </a>

                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

